import { useState, useMemo } from 'react';
import { DRAW_TEMPLATES, TEMPLATE_CATEGORIES, getTemplatesByCategory, searchTemplates } from '@/lib/templates/draw-templates';
import useDrawStore from '@/stores/drawStore';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  X,
  LayoutTemplate,
  Check,
  Sparkles,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function TemplateLibrary({ open, onOpenChange }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  
  const { loadTemplateElements, documentName, elements } = useDrawStore();

  // Filter templates based on search and category
  const filteredTemplates = useMemo(() => {
    let templates = searchQuery ? searchTemplates(searchQuery) : DRAW_TEMPLATES;
    
    if (activeCategory !== 'all') {
      templates = templates.filter(t => t.category === activeCategory);
    }
    
    return templates;
  }, [searchQuery, activeCategory]);

  // Get category info
  const getCategoryInfo = (categoryId) => {
    return TEMPLATE_CATEGORIES.find(c => c.id === categoryId);
  };

  // Handle template selection
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setPreviewTemplate(template);
  };

  // Apply selected template
  const handleApplyTemplate = () => {
    if (!selectedTemplate) return;
    
    // Check if canvas has elements
    if (elements.length > 0) {
      if (!window.confirm('Cette action va remplacer le contenu actuel. Voulez-vous continuer ?')) {
        return;
      }
    }
    
    // Load template elements into the store
    loadTemplateElements(selectedTemplate.elements, selectedTemplate.name);
    
    toast.success(`Template "${selectedTemplate.name}" appliqué !`);
    onOpenChange(false);
    setSelectedTemplate(null);
    setPreviewTemplate(null);
  };

  // Render template card
  const renderTemplateCard = (template) => {
    const categoryInfo = getCategoryInfo(template.category);
    const isSelected = selectedTemplate?.id === template.id;
    
    return (
      <div
        key={template.id}
        className={cn(
          "group relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200",
          "hover:shadow-lg hover:border-primary/50",
          isSelected 
            ? "border-primary bg-primary/5 shadow-md" 
            : "border-border bg-card hover:bg-accent/50"
        )}
        onClick={() => handleSelectTemplate(template)}
        data-testid={`template-${template.id}`}
      >
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow-lg">
            <Check className="h-4 w-4 text-primary-foreground" />
          </div>
        )}
        
        {/* Template preview area */}
        <div className={cn(
          "relative h-32 rounded-lg mb-3 overflow-hidden",
          "bg-gradient-to-br from-muted to-muted/50"
        )}>
          {/* Mini canvas preview representation */}
          <div className="absolute inset-2 flex items-center justify-center">
            <div className="text-4xl">{categoryInfo?.icon || '📋'}</div>
          </div>
          
          {/* Hover overlay */}
          <div className={cn(
            "absolute inset-0 bg-primary/10 opacity-0 transition-opacity",
            "group-hover:opacity-100 flex items-center justify-center"
          )}>
            <span className="text-xs font-medium text-primary bg-background/90 px-2 py-1 rounded">
              Cliquez pour prévisualiser
            </span>
          </div>
        </div>
        
        {/* Template info */}
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-semibold text-sm leading-tight line-clamp-2">
              {template.name}
            </h4>
          </div>
          
          <p className="text-xs text-muted-foreground line-clamp-2">
            {template.description}
          </p>
          
          <div className="flex items-center gap-2">
            <Badge 
              variant="outline" 
              className="text-[10px] px-1.5 py-0"
              style={{ 
                borderColor: categoryInfo?.color,
                color: categoryInfo?.color 
              }}
            >
              {categoryInfo?.name}
            </Badge>
            <span className="text-[10px] text-muted-foreground">
              {template.elements.length} éléments
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <LayoutTemplate className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl">Galerie de Templates</DialogTitle>
              <DialogDescription>
                Choisissez un template pour démarrer votre diagramme
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left sidebar - Categories */}
          <div className="w-56 border-r bg-muted/30 p-4 flex flex-col">
            <div className="space-y-1">
              <button
                className={cn(
                  "w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  activeCategory === 'all' 
                    ? "bg-primary text-primary-foreground" 
                    : "hover:bg-muted"
                )}
                onClick={() => setActiveCategory('all')}
              >
                <div className="flex items-center justify-between">
                  <span>Tous les templates</span>
                  <Badge variant="secondary" className="text-xs">
                    {DRAW_TEMPLATES.length}
                  </Badge>
                </div>
              </button>
              
              <div className="py-2">
                <div className="h-px bg-border" />
              </div>
              
              {TEMPLATE_CATEGORIES.map((category) => {
                const count = DRAW_TEMPLATES.filter(t => t.category === category.id).length;
                return (
                  <button
                    key={category.id}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                      activeCategory === category.id 
                        ? "bg-primary text-primary-foreground" 
                        : "hover:bg-muted"
                    )}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    <div className="flex items-center gap-2">
                      <span>{category.icon}</span>
                      <span className="flex-1 truncate">{category.name}</span>
                      <Badge 
                        variant={activeCategory === category.id ? "secondary" : "outline"} 
                        className="text-[10px] px-1.5"
                      >
                        {count}
                      </Badge>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Middle - Template grid */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un template..."
                  className="pl-9 pr-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="template-search"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              {searchQuery && (
                <p className="text-xs text-muted-foreground mt-2">
                  {filteredTemplates.length} résultat(s) pour "{searchQuery}"
                </p>
              )}
            </div>

            {/* Templates grid */}
            <ScrollArea className="flex-1">
              <div className="p-4">
                {filteredTemplates.length > 0 ? (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredTemplates.map(renderTemplateCard)}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="p-4 rounded-full bg-muted mb-4">
                      <Search className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="font-semibold text-lg mb-1">Aucun template trouvé</h3>
                    <p className="text-muted-foreground text-sm">
                      Essayez avec d'autres termes de recherche
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Right sidebar - Preview */}
          {previewTemplate && (
            <div className="w-80 border-l bg-muted/30 p-4 flex flex-col">
              <h3 className="font-semibold mb-3">Aperçu</h3>
              
              {/* Preview area */}
              <div className="relative h-48 rounded-lg bg-background border mb-4 overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-6xl opacity-50">
                    {getCategoryInfo(previewTemplate.category)?.icon}
                  </div>
                </div>
                <div className="absolute bottom-2 right-2">
                  <Badge variant="secondary" className="text-xs">
                    {previewTemplate.elements.length} éléments
                  </Badge>
                </div>
              </div>
              
              {/* Template details */}
              <div className="space-y-4 flex-1">
                <div>
                  <h4 className="font-semibold text-lg">{previewTemplate.name}</h4>
                  <Badge 
                    className="mt-1"
                    style={{ 
                      backgroundColor: getCategoryInfo(previewTemplate.category)?.color + '20',
                      color: getCategoryInfo(previewTemplate.category)?.color,
                      borderColor: getCategoryInfo(previewTemplate.category)?.color 
                    }}
                  >
                    {getCategoryInfo(previewTemplate.category)?.name}
                  </Badge>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  {previewTemplate.description}
                </p>
                
                <div className="space-y-2">
                  <h5 className="text-xs font-medium uppercase text-muted-foreground">
                    Contenu du template
                  </h5>
                  <ul className="text-sm space-y-1">
                    <li className="flex items-center gap-2">
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      <span>
                        {previewTemplate.elements.filter(e => e.type === 'rectangle').length} rectangles
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      <span>
                        {previewTemplate.elements.filter(e => e.type === 'text').length} textes
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      <span>
                        {previewTemplate.elements.filter(e => e.type === 'arrow').length} flèches
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      <span>
                        {previewTemplate.elements.filter(e => e.type === 'circle').length} cercles
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Apply button */}
              <Button
                className="w-full gap-2 mt-4"
                size="lg"
                onClick={handleApplyTemplate}
                data-testid="apply-template-btn"
              >
                <Sparkles className="h-4 w-4" />
                Utiliser ce template
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t bg-muted/30">
          <div className="flex items-center justify-between w-full">
            <p className="text-sm text-muted-foreground">
              {selectedTemplate 
                ? `Template sélectionné : ${selectedTemplate.name}` 
                : 'Sélectionnez un template pour commencer'}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleApplyTemplate}
                disabled={!selectedTemplate}
                data-testid="confirm-template-btn"
              >
                Appliquer le template
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
